import axios, { type AxiosResponse } from 'axios'
import key from 'utils/localStorageKeys'
import { axiosInstance } from './axiosInstance'
import { END_POINT_PRODUCT } from './index'
import { pushToDataLayer } from 'utils/eventPushes'

// ─────────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────────

export interface PaginationParams {
  page?: number
  size?: number
  /** Format: "createdDate,desc" */
  sort?: string
}

export interface SummaryWarning {
  message: string
  materialId?: string
  priceItemName?: string
}

export interface SummaryGroupResult<T = unknown> {
  data: Record<string, T[]>
  warnings: SummaryWarning[]
}

export interface SummaryFilter {
  pageIds?: string[]
  folderIds?: string[]
  sectionNames?: string[]
  materialIds?: string[]
  pricedOnly?: boolean
  unpricedOnly?: boolean
  tags?: string[]
  /** Individual measurements (takeoff shape ids); empty or absent keeps every shape. */
  shapeIds?: string[]
  /** Keep manual takeoff items through page and shape filters. */
  includeManualItems?: boolean
}

export interface ProposalDocumentUpdate {
  client?: {
    id?: string | null
    name?: string
    address?: string
    city?: string
    phone?: string
    email?: string
  } | null
  contactId?: string | null
  expiryDate?: string | null
  notes?: string | null
  extendedNotes?: string | null
  intro?: string | null
  inclusions?: string[] | null
  exclusions?: string[] | null
  assumptions?: string[] | null
  showIntro?: boolean | null
  showInclusionsExclusions?: boolean | null
  showAssumptions?: boolean | null
  showAcceptance?: boolean | null
  showAcceptedBy?: boolean | null
  showAcceptanceDate?: boolean | null
  discount?: { type: 'percentage' | 'fixed'; value: number } | null
  tax?: number | null
  status?: string | null
}

export interface LineItemOverride {
  lineItemKey: string
  visible?: boolean | null
  displayName?: string | null
  displayDescription?: string | null
  showCostBreakdown?: boolean | null
  order?: number | null
}

export interface SectionOverride {
  visible?: boolean | null
  displayName?: string | null
  order?: number | null
}

export interface ProposalVersionMeta {
  label?: string | null
  status?: string | null
  includedDrawings: boolean
  snapshotTotal?: number | null
  snapshotOrderNumber?: number | null
  snapshotClientName?: string | null
}

export interface ProposalVersion {
  id: string
  proposalId: string
  versionNumber: number
  pdfUrl: string
  fileSizeBytes: number | null
  label: string | null
  status: string | null
  includedDrawings: boolean
  snapshotTotal: number | null
  snapshotOrderNumber: number | null
  snapshotClientName: string | null
  createdAt: string
  createdBy: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Project summary / proposals (legacy)
// ─────────────────────────────────────────────────────────────────────────────

export function getProjectSummary(projectId: string, id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposal/${projectId}/${id}`,
  })
}

export function getProjectSummaryList(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/summary/${id}`,
  })
}

export function getProposals(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposals/${id}`,
  })
}

export function resetProposal(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal/reset/${id}`,
  })
}

export function createProposalByProject(projectId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposal/${projectId}`,
  })
}

export function getDeafultProposal(projectId: string): Promise<AxiosResponse> {
  if ((key as any).TOKEN) {
    return axiosInstance.request({
      method: 'get',
      url: `${END_POINT_PRODUCT}/api/default-proposal/${projectId}`,
    })
  }
  return axios.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/default-proposal/${projectId}`,
  })
}

export function deleteProposal(projectId: string, id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'delete',
    url: `${END_POINT_PRODUCT}/api/proposal/${projectId}/${id}`,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Company details
// ─────────────────────────────────────────────────────────────────────────────

export function getCompanyDetails(): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/company-details`,
  })
}

export function getCompanyDetailsByProjectId(projectId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/company-details/${projectId}`,
  })
}

export function updateCompanyDetails(data: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/takeoffs/company-details`,
    data,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Legacy proposal-materials endpoints
// ─────────────────────────────────────────────────────────────────────────────

export function proposalStart(projectId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposal-start/${projectId}`,
  })
}

export function getProposalMaterials(proposalId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposal-materials/${proposalId}`,
  })
}

export function propsalMaterials(data: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposal-materials`,
    data,
  })
}

export function updateProposalMaterials(data: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-materials`,
    data,
  })
}

export function deleteProposalMaterial(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'delete',
    url: `${END_POINT_PRODUCT}/api/proposal-materials/${id}`,
  })
}

export function proposalGenerate(proposalId: string, projectId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-generate/${proposalId}/${projectId}`,
  })
}

export function generateProposalV2(projectId: string, data: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposal/generate/v2/${projectId}`,
    data,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Summary group endpoints (with response normalization)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalizes the summary-group response into a consistent shape:
 *   { data: Record<string, item[]>, warnings: SummaryWarning[] }
 *
 * Handles both the new wrapped {data, warnings} shape and the legacy
 * top-level shape. Strips any non-array keys defensively.
 */
function normalizeSummaryResponse<T = unknown>(body: unknown): SummaryGroupResult<T> {
  let dataMap: Record<string, unknown> = {}
  let warnings: SummaryWarning[] = []

  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    const wrapped = body as { data?: unknown; warnings?: unknown }
    dataMap = (wrapped.data as Record<string, unknown>) ?? {}
    warnings = Array.isArray(wrapped.warnings) ? (wrapped.warnings as SummaryWarning[]) : []
  } else {
    dataMap = (body as Record<string, unknown>) ?? {}
  }

  const cleanData: Record<string, T[]> = {}
  for (const [k, v] of Object.entries(dataMap)) {
    if (Array.isArray(v)) {
      cleanData[k] = v as T[]
    } else {
      warnings.push({ message: `Skipped non-array key "${k}" in summary response` })
    }
  }

  return { data: cleanData, warnings }
}

export const getSummaryGroup = async <T = unknown>(projectId: string): Promise<SummaryGroupResult<T>> => {
  const response = await axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/summary-group/${projectId}`,
  })
  return normalizeSummaryResponse<T>(response.data)
}

export function getSummaryGroupSample(projectId: string, drawingPages: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/summary-group-sample/${projectId}`,
    data: drawingPages,
  })
}

/**
 * Filtered summary group. Sends filter object (pageIds, folderIds, sectionNames,
 * materialIds, pricedOnly, unpricedOnly) to the backend, which applies filters
 * at the shape level before aggregating. Empty/missing filter object returns
 * all results.
 */
export const getSummaryGroupFiltered = async <T = unknown>(
  projectId: string,
  filter: SummaryFilter = {},
): Promise<SummaryGroupResult<T>> => {
  const response = await axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/summary-group/${projectId}/filter`,
    data: filter,
  })
  return normalizeSummaryResponse<T>(response.data)
}

// ─────────────────────────────────────────────────────────────────────────────
// Project-agnostic proposal endpoints
// ─────────────────────────────────────────────────────────────────────────────

export function getAllProposals(params: PaginationParams = {}): Promise<AxiosResponse> {
  const requestParams: Record<string, unknown> = {
    page: params.page ?? 0,
    size: params.size ?? 16,
  }

  if (params.sort) {
    requestParams.sort = params.sort
  }

  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposals`,
    params: requestParams,
  })
}

export function createNewProposal(): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposals`,
  })
}

export function getProposalById(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposals/v2/${id}`,
  })
}

export function updateProposal(data: unknown): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposals/v2`,
    data,
  })
}

export function deleteProposalById(id: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'delete',
    url: `${END_POINT_PRODUCT}/api/proposals/v2/${id}`,
  })
}

/**
 * Get proposals by opportunity ID
 * GET /api/proposals/opportunity/{opportunityId}
 */
export function getProposalsByOpportunityId(opportunityId: string, params: PaginationParams = {}): Promise<AxiosResponse> {
  const requestParams: Record<string, unknown> = {
    page: params.page ?? 0,
    size: params.size ?? 16,
  }

  if (params.sort) {
    requestParams.sort = params.sort
  }

  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposals/opportunity/${opportunityId}`,
    params: requestParams,
  })
}

/**
 * Assign a proposal to an opportunity
 * PUT /api/proposals/{proposalId}/opportunity
 */
export async function assignProposalToOpportunity(proposalId: string, opportunityId: string): Promise<AxiosResponse> {
  const response = await axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposals/${proposalId}/opportunity`,
    data: opportunityId,
    headers: {
      'Content-Type': 'text/plain',
    },
  })

  pushToDataLayer({
    event: 'proposal_added_to_opportunity',
    proposal_id: proposalId,
    opportunity_id: opportunityId,
    timestamp: new Date().toISOString(),
  })

  return response
}

// ─────────────────────────────────────────────────────────────────────────────
// Proposal View — live, read-on-load, overrides persist on edit
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch the live proposal view. Generates fresh from current takeoff/pricing
 * and overlays any persisted curation. No write — safe to call on tab open.
 */
export function getProposalView(projectId: string): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposal-view/${projectId}`,
  })
}

/**
 * Patch document-level proposal fields (client, dates, notes, discount, tax,
 * status). Send only the fields you want to update — others remain unchanged.
 * Returns the regenerated view with fresh totals.
 */
export function updateProposalDocument(projectId: string, update: ProposalDocumentUpdate): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-view/${projectId}/document`,
    data: update,
  })
}

/**
 * Upsert a single line-item override (visibility, displayName, displayDescription,
 * showCostBreakdown, order). Pass nulls for fields you want to clear back to
 * default. Returns the regenerated view.
 */
export function updateLineItemOverride(projectId: string, override: LineItemOverride): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-view/${projectId}/line-items`,
    data: override,
  })
}

/**
 * Bulk update — used for drag-to-reorder where many items get new orders in one
 * pass. One round-trip instead of N.
 */
export function updateLineItemOverridesBulk(projectId: string, overrides: LineItemOverride[]): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-view/${projectId}/line-items/bulk`,
    data: overrides,
  })
}

/**
 * Override a single section (rename, hide, reorder).
 */
export function updateSectionOverride(
  projectId: string,
  sectionName: string,
  override: SectionOverride,
): Promise<AxiosResponse> {
  return axiosInstance.request({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/proposal-view/${projectId}/sections/${encodeURIComponent(sectionName)}`,
    data: override,
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Proposal PDF version history
// ─────────────────────────────────────────────────────────────────────────────

export async function saveProposalVersion(
  proposalId: string,
  pdfBlob: Blob,
  meta: ProposalVersionMeta,
): Promise<ProposalVersion> {
  const form = new FormData()
  form.append('file', pdfBlob, `proposal-${proposalId}.pdf`)
  form.append('meta', new Blob([JSON.stringify(meta)], { type: 'application/json' }))

  const { data } = await axiosInstance.request({
    method: 'post',
    url: `${END_POINT_PRODUCT}/api/proposals/${proposalId}/versions`,
    data: form,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return data as ProposalVersion
}

export async function listProposalVersions(proposalId: string): Promise<ProposalVersion[]> {
  const { data } = await axiosInstance.request({
    method: 'get',
    url: `${END_POINT_PRODUCT}/api/proposals/${proposalId}/versions`,
  })
  return data as ProposalVersion[]
}

export async function updateProposalVersion(
  historyId: string,
  body: { label?: string; status?: string },
): Promise<ProposalVersion> {
  const { data } = await axiosInstance.request({
    method: 'patch',
    url: `${END_POINT_PRODUCT}/api/proposals/versions/${historyId}`,
    data: body,
  })
  return data as ProposalVersion
}

export async function deleteProposalVersion(historyId: string): Promise<void> {
  await axiosInstance.request({
    method: 'delete',
    url: `${END_POINT_PRODUCT}/api/proposals/versions/${historyId}`,
  })
}
